import { transportDocumentTypes, type TransportDocumentType } from '../document/document-types';
import type { TradeInput, TradeItemInput } from './schema';

const all = transportDocumentTypes;
const bl = ['BILL_OF_LADING'] as const;
const awb = ['AIR_WAYBILL'] as const;
const manifest = ['MANIFEST'] as const;
const advice = ['SHIPPING_ADVICE'] as const;
const si = ['SHIPPING_INSTRUCTION'] as const;
const sea = [...bl, ...manifest, ...advice, ...si];
const oceanCargo = [...bl, ...si];
const parties = [...bl, ...awb, ...si];

export type TransportField = Readonly<{
  field: Exclude<keyof TradeInput, 'buyer' | 'supplier' | 'items'>;
  label: string;
  types: readonly TransportDocumentType[];
  section: '单证信息' | '运输与相关方' | '出单要求' | '空运费用' | '声明与签发';
  inputType?: 'date' | 'decimal';
  multiline?: boolean;
  options?: readonly string[];
}>;

export const transportFields: readonly TransportField[] = [
  { field: 'transportDate', label: '单证日期 / Date', types: all, section: '单证信息', inputType: 'date' },
  { field: 'billOfLadingNumber', label: '提单号 / B/L No.', types: [...bl, ...advice], section: '单证信息' },
  { field: 'awbNumber', label: '空运单号 / AWB No.', types: awb, section: '单证信息' },
  { field: 'bookingNumber', label: '订舱号 / Booking No.', types: si, section: '单证信息' },
  { field: 'invoiceNumber', label: '发票号 / Invoice No.', types: advice, section: '单证信息' },
  { field: 'contractNumber', label: '合同号 / Contract No.', types: advice, section: '单证信息' },
  { field: 'ourReference', label: '我方编号 / Our Ref.', types: advice, section: '单证信息' },
  { field: 'placeOfIssue', label: '签发地 / Place of Issue', types: bl, section: '单证信息' },
  { field: 'currency', label: '币种 / Currency', types: [...awb, ...advice], section: '单证信息', options: ['USD', 'EUR', 'GBP', 'HKD', 'CNY'] },
  { field: 'forwarder', label: '致货代 / 船公司 TO', types: si, section: '运输与相关方', multiline: true },
  { field: 'shipper', label: '托运人 / Shipper（名称、地址、电话）', types: parties, section: '运输与相关方', multiline: true },
  { field: 'consignee', label: '收货人 / Consignee（名称、地址、电话）', types: [...parties, ...advice], section: '运输与相关方', multiline: true },
  { field: 'notifyParty', label: '通知方 / Notify Party', types: parties, section: '运输与相关方', multiline: true },
  { field: 'vessel', label: '船名 / Vessel', types: sea, section: '运输与相关方' },
  { field: 'voyageNumber', label: '航次 / Voyage', types: sea, section: '运输与相关方' },
  { field: 'portOfLoading', label: '起运港 / Port of Loading', types: sea, section: '运输与相关方' },
  { field: 'portOfDischarge', label: '卸货港 / Port of Discharge', types: sea, section: '运输与相关方' },
  { field: 'placeOfDelivery', label: '交货地 / Place of Delivery', types: oceanCargo, section: '运输与相关方' },
  { field: 'finalDestination', label: '最终目的地 / Final Destination', types: bl, section: '运输与相关方' },
  { field: 'shippingMarks', label: '整票唛头 / Marks & Nos.', types: bl, section: '运输与相关方', multiline: true },
  { field: 'etd', label: '离港时间 / ETD（装运通知填实际离港）', types: [...manifest, ...advice, ...si], section: '运输与相关方' },
  { field: 'eta', label: '预计到港 / ETA', types: [...manifest, ...advice], section: '运输与相关方' },
  { field: 'cutOff', label: '截关时间 / Cut-off', types: si, section: '运输与相关方' },
  { field: 'departureAirport', label: '始发机场 / Airport of Departure', types: awb, section: '运输与相关方' },
  { field: 'destinationAirport', label: '目的机场 / Airport of Destination', types: awb, section: '运输与相关方' },
  { field: 'flightNumber', label: '航班号 / Flight No.', types: awb, section: '运输与相关方' },
  { field: 'flightDate', label: '航班日期 / Flight Date', types: awb, section: '运输与相关方', inputType: 'date' },
  { field: 'voyageType', label: '航次类型 / Voyage Type', types: manifest, section: '运输与相关方', options: ['EXPORT', 'IMPORT'] },
  { field: 'carrierName', label: '船公司 / Carrier', types: manifest, section: '运输与相关方' },
  { field: 'masterName', label: '船长 / Master', types: manifest, section: '运输与相关方' },
  { field: 'countryOfOrigin', label: '原产国 / Country of Origin', types: advice, section: '运输与相关方' },
  { field: 'totalContainers', label: '总柜数 / Total Containers', types: [...oceanCargo, ...manifest], section: '出单要求' },
  { field: 'blServiceType', label: '出单类型 / B/L Type', types: oceanCargo, section: '出单要求', options: ['ORIGINAL', 'TELEX RELEASE', 'SEAWAY'] },
  { field: 'originals', label: '正本份数 / No. of Originals', types: oceanCargo, section: '出单要求' },
  { field: 'freightTerms', label: '运费条款 / Freight Terms', types: oceanCargo, section: '出单要求' },
  { field: 'blConsignee', label: '提单抬头 / B/L Consignee', types: si, section: '出单要求', multiline: true },
  { field: 'blNotifyParty', label: '提单通知方 / B/L Notify Party', types: si, section: '出单要求', multiline: true },
  { field: 'releaseClause', label: '放货条款 / Release Clause', types: si, section: '出单要求', multiline: true },
  { field: 'specialRequirements', label: '特别指示 / Special Instructions', types: si, section: '出单要求', multiline: true },
  { field: 'declaredValueCarriage', label: '运输声明价值 / Declared Value for Carriage', types: awb, section: '出单要求' },
  { field: 'declaredValueCustoms', label: '海关声明价值 / Declared Value for Customs', types: awb, section: '出单要求' },
  { field: 'handlingInformation', label: '操作信息 / Handling Information', types: awb, section: '出单要求', multiline: true },
  { field: 'weightChargeCode', label: '运费代码 / Weight Charge Code', types: awb, section: '空运费用' },
  { field: 'weightPrepaid', label: '运费预付 / Weight Prepaid', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'weightCollect', label: '运费到付 / Weight Collect', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'otherChargeCode', label: '其他费用代码 / Other Charge Code', types: awb, section: '空运费用' },
  { field: 'otherPrepaid', label: '其他费预付 / Other Prepaid', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'otherCollect', label: '其他费到付 / Other Collect', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'taxChargeCode', label: '税费代码 / Tax Charge Code', types: awb, section: '空运费用' },
  { field: 'taxPrepaid', label: '税费预付 / Tax Prepaid', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'taxCollect', label: '税费到付 / Tax Collect', types: awb, section: '空运费用', inputType: 'decimal' },
  { field: 'manifestDeclaration', label: '申报声明原文 / Declaration', types: manifest, section: '声明与签发', multiline: true },
  { field: 'preparedBy', label: '编制人 / Prepared by', types: manifest, section: '声明与签发' },
  { field: 'dispatchedDocuments', label: '已寄单据 / Documents Sent', types: advice, section: '声明与签发', multiline: true },
  { field: 'courierNumber', label: '快递单号 / Courier No.', types: advice, section: '声明与签发' },
  { field: 'dispatchDate', label: '寄出日期 / Sent Date', types: advice, section: '声明与签发', inputType: 'date' },
  { field: 'carrierSignature', label: '承运人签章 / Carrier Signature', types: [...bl, ...awb, ...manifest], section: '声明与签发' },
  { field: 'authorizedSignature', label: '签发人 / Issued by', types: [...advice, ...si], section: '声明与签发' },
  { field: 'issuerPhone', label: '签发人电话 / Tel', types: si, section: '声明与签发' },
  { field: 'note', label: '备注 / Remarks', types: all, section: '声明与签发', multiline: true },
];

export const transportItemFields = [
  { field: 'billOfLadingNumber', label: '提单号 / B/L No.', types: manifest },
  { field: 'shipper', label: '托运人 / Shipper', types: manifest },
  { field: 'consignee', label: '收货人 / Consignee', types: manifest },
  { field: 'name', label: '品名', types: all },
  { field: 'description', label: '品名规格', types: all },
  { field: 'model', label: '型号', types: all },
  { field: 'quantity', label: '数量 / 件数', types: all },
  { field: 'unit', label: '单位', types: advice },
  { field: 'unitPrice', label: '单价', types: advice },
  { field: 'hsCode', label: 'HS 编码', types: [...si, ...advice] },
  { field: 'packages', label: '包装 / Package', types: [...oceanCargo, ...advice] },
  { field: 'grossWeight', label: '毛重 (kg)', types: all },
  { field: 'netWeight', label: '净重 (kg)', types: [...oceanCargo, ...advice] },
  { field: 'measurement', label: '体积 (m³)', types: all },
  { field: 'chargeableWeight', label: '计费重量 (kg)', types: awb },
  { field: 'container', label: '柜号 / Container No.', types: [...oceanCargo, ...manifest] },
  { field: 'sealNumber', label: '封号 / Seal No.', types: [...oceanCargo, ...manifest] },
  { field: 'containerType', label: '柜型 / Container Type', types: si },
  { field: 'marks', label: '唛头 / Marks', types: sea },
  { field: 'remark', label: '货物备注', types: si },
] as const satisfies readonly { field: keyof TradeItemInput; label: string; types: readonly TransportDocumentType[] }[];

export function transportFieldsFor(type: TransportDocumentType) {
  return transportFields.filter(field => field.types.includes(type));
}
export function transportItemFieldsFor(type: TransportDocumentType) {
  return transportItemFields.filter(field => (field.types as readonly string[]).includes(type));
}
